/**
 * ListView 표현 계층: 리스트 프레임·행·상태·무한스크롤 marker의 styled Shell.
 * styled-components 본문(RFC-design-system-split 스타일링 전략). 색은 semantic token만
 * 사용하고 spacing은 px 리터럴 인라인(c6엔 spacing 토큰 없음 — RFC-data-table).
 */
import type { CSSProperties } from "react";
import styled, { type DefaultTheme } from "styled-components";
import { resolveListViewAppearanceRecipe } from "./appearance";
import type {
  ListViewAppearance,
  ListViewRowAppearance,
  ListViewRowPaddingInline,
  ListViewVariant,
} from "./ListView";

import {
  controlFocusRing,
  controlTransition,
  inlineElementHeight,
} from "../foundations/controlStyles";
import {
  interactiveSurfaceCss,
  type InteractiveSurfaceSpec,
} from "../foundations/interactiveSurface";

export type ListViewCssProperties = CSSProperties & {
  "--c6-list-view-row-height"?: string;
  "--c6-list-view-virtual-height"?: string;
  "--c6-list-view-row-offset"?: string;
};

export const listViewRowHeightByVariant = {
  // Annotation Studio의 regular composite row와 같은 border-box 높이.
  comfortable: "45px",
  compact: inlineElementHeight.md,
} as const;

/**
 * 가상화 수학이 쓰는 variant별 정수 px 행 높이. compact는 inlineElementHeight.md(2rem)의
 * 16px 기준 환산값이며, 가상화 모드에선 같은 값을 CSS 변수로도 강제해 수학과
 * 렌더가 root font-size와 무관하게 일치한다.
 */
export const listViewVirtualRowHeightByVariant = {
  comfortable: 45,
  compact: 32,
} as const;

// 고정 높이 행과 같은 px 좌표계로 CSS와 virtualizer가 공유한다.
export const listViewRaisedGeometry = { gap: 2, leadingInset: 4 } as const;

/**
 * 가상화 수학이 쓰는 정규 행 기하(px). rowHeight는 행 하나의 border-box 높이,
 * rowPitch는 행 시작점 사이 거리(rowHeight + rowGap)다. index↔scroll 좌표 변환은
 * rowPitch, 행 자체의 크기가 필요한 자리는 rowHeight를 쓴다.
 */
export type ListViewRowGeometry = {
  rowHeight: number;
  rowGap: number;
  rowPitch: number;
  leadingInset: number;
};

export function resolveListViewRowGeometry(
  variant: ListViewVariant,
  rowAppearance: ListViewRowAppearance,
): ListViewRowGeometry {
  const rowHeight = listViewVirtualRowHeightByVariant[variant];
  const raised = rowAppearance === "raised";
  const rowGap = raised ? listViewRaisedGeometry.gap : 0;
  return {
    rowHeight,
    rowGap,
    rowPitch: rowHeight + rowGap,
    leadingInset: raised ? listViewRaisedGeometry.leadingInset : 0,
  };
}

function transparentPrimarySurface(theme: DefaultTheme): InteractiveSurfaceSpec {
  const layer = {
    background: "transparent",
    border: "transparent",
    foreground: theme.color.text.default,
  };
  return {
    rest: layer,
    hover: layer,
    active: layer,
    disabled: { ...layer, foreground: theme.color.text.disabled },
  };
}

export const ListViewShell = styled.div<{
  $appearance: ListViewAppearance;
  $rowAppearance: ListViewRowAppearance;
  $rowPaddingInline: ListViewRowPaddingInline;
}>`
  ${({ theme, $appearance, $rowAppearance, $rowPaddingInline }) => {
    const { frame, row } = resolveListViewAppearanceRecipe(theme, $appearance, $rowAppearance);
    return `
      --c6-list-view-frame-border: ${frame.borderWidth} solid ${frame.borderColor};
      --c6-list-view-frame-radius: ${frame.radius};
      --c6-list-view-frame-surface: ${frame.surface};
      --c6-list-view-row-radius: ${row.radius};
      --c6-list-view-row-divider: ${row.divider};
      --c6-list-view-row-rest: ${row.surface.rest};
      --c6-list-view-row-hover: ${row.surface.hover};
      --c6-list-view-row-active: ${row.surface.active};
      --c6-list-view-row-selected: ${row.surface.selected};
      --c6-list-view-row-selected-hover: ${row.surface.selectedHover};
      --c6-list-view-row-selected-active: ${row.surface.selectedActive};
      --c6-list-view-padding-inline: ${$rowPaddingInline}px;
    `;
  }}
  /* 래퍼가 준 높이에 프레임 테두리까지 포함해야 소비처의 행 단위 계산이 맞는다(전역 리셋 없음). */
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  border: var(--c6-list-view-frame-border);
  border-radius: var(--c6-list-view-frame-radius);
  background: var(--c6-list-view-frame-surface);
  overflow: hidden;

  .header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px var(--c6-list-view-padding-inline);
    border-bottom: 1px solid ${({ theme }) => theme.color.border.default};
    background: ${({ theme }) => theme.color.bg.surface};
  }

  .pinnedGroup {
    background: var(--c6-list-view-row-rest);
  }

  .pinnedGroup .item[data-last="true"] {
    border-bottom-color: ${({ theme }) => theme.color.border.default};
  }

  /* block flow를 유지해야 bottom sentinel이 모든 행 뒤의 실제 바닥에 놓인다. */
  .scroller {
    flex: 1;
    min-height: 0;
  }

  .collection {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  .virtualCollection {
    position: relative;
    height: var(--c6-list-view-virtual-height);
  }

  .message {
    padding: 16px;
    color: ${({ theme }) => theme.color.text.muted};
    text-align: center;
  }

  .message[data-tone="error"] {
    color: ${({ theme }) => theme.color.text.danger};
  }

  .message > p {
    color: inherit;
  }

  .messageAction {
    display: flex;
    justify-content: center;
    margin-top: 12px;
  }

  .loader {
    min-height: 100%;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px;
    color: ${({ theme }) => theme.color.text.muted};
  }

  .item {
    position: relative;
    box-sizing: border-box;
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    width: 100%;
    min-width: 0;
    min-height: ${listViewRowHeightByVariant.comfortable};
    align-items: stretch;
    border-bottom: 1px solid var(--c6-list-view-row-divider);
    color: ${({ theme }) => theme.color.text.default};
    background: var(--c6-list-view-row-rest);
    ${controlTransition("background-color")}
  }

  &[data-variant="compact"] .item {
    min-height: ${listViewRowHeightByVariant.compact};
  }

  .item:hover {
    background: var(--c6-list-view-row-hover);
  }

  .item[data-selected="true"] {
    background: var(--c6-list-view-row-selected);
  }

  .item[data-selected="true"]:hover {
    background: var(--c6-list-view-row-selected-hover);
  }

  .item:has(.primary:active) {
    background: var(--c6-list-view-row-active);
  }

  .item[data-selected="true"]:has(.primary:active) {
    background: var(--c6-list-view-row-selected-active);
  }

  .item[data-last="true"] {
    border-bottom-color: transparent;
  }

  /* Off-filter context는 selection과 독립된 attention 면이다. 선택이 결과 행으로 이동해도
     상단에 유지된 행의 이유가 색으로 남고, pinned 선택은 같은 amber 축에서 한 단계 오른다. */
  .item[data-pinned="true"] {
    background: ${({ theme }) => theme.color.bg.attention};
  }

  .item[data-pinned="true"]:hover,
  .item[data-pinned="true"][data-selected="true"] {
    background: ${({ theme }) => theme.color.bg.attentionHover};
  }

  .item[data-pinned="true"][data-selected="true"]:hover,
  .item[data-pinned="true"]:has(.primary:active) {
    background: ${({ theme }) => theme.color.bg.attentionActive};
  }

  .item::before {
    position: absolute;
    z-index: 3;
    inset-block: 0;
    inset-inline-start: 0;
    width: 3px;
    background: ${({ theme }) => theme.color.border.selected};
    content: "";
    opacity: 0;
    pointer-events: none;
    ${controlTransition("opacity")}
  }

  .item[data-selected="true"]::before {
    opacity: 1;
  }

  .primaryCell {
    position: relative;
    display: grid;
    min-width: 0;
    align-items: stretch;
  }

  .primary {
    position: absolute;
    z-index: 1;
    inset: 0;
    box-sizing: border-box;
    display: block;
    width: 100%;
    min-width: 0;
    height: 100%;
    padding: 0;
    border: 0;
    border-radius: 0;
    font: inherit;
    text-align: left;
    ${({ theme }) => interactiveSurfaceCss(transparentPrimarySurface(theme))}
  }

  .primary:focus-visible {
    ${controlFocusRing("-2px")}
  }

  .content {
    z-index: 2;
    box-sizing: border-box;
    display: grid;
    min-width: 0;
    height: 100%;
    align-items: center;
    padding: 10px var(--c6-list-view-padding-inline);
    pointer-events: none;
  }

  .actions {
    z-index: 3;
    display: flex;
    min-width: 0;
    align-items: stretch;
    gap: 0;
  }

  /* Studio composite list와 같은 edge-aligned action cell: 액션의 의미·내용은 소비자가
     소유하되 행에 결합되는 형상은 ListView가 소유한다. */
  .actions > span {
    display: flex;
    width: 2rem;
    min-width: 2rem;
    height: 100%;
    align-items: stretch;
  }

  .actions button {
    box-sizing: border-box;
    width: 2rem;
    min-width: 2rem;
    height: 100%;
    border-radius: 0;
  }

  .actions button:focus-visible {
    ${controlFocusRing("-2px")}
  }

  /* action의 hover/active는 행 상태 위에 얹는 목록 피드백(alpha state layer)이다 — 선택 행에서도
     selected 사다리를 기준으로 읽히고, 버튼 자체의 불투명 surface 톤이 행 색을 가리지 않는다
     (Studio composite list와 같은 계약). background-color만 덮어 semantic background image는 보존. */
  .actions button:hover:enabled {
    background-color: ${({ theme }) => theme.color.bg.muted};
  }

  .actions button:active:enabled {
    background-color: ${({ theme }) => theme.color.bg.selectedActive};
    transition-duration: 0ms;
  }

  .actions button[data-studio-list-warning]:hover:enabled {
    background-color: ${({ theme }) => theme.color.bg.control.warningHover};
    color: ${({ theme }) => theme.color.text.onControl};
  }

  .actions button[data-studio-list-warning]:active:enabled {
    background-color: ${({ theme }) => theme.color.bg.control.warningActive};
    color: ${({ theme }) => theme.color.text.onControl};
    transition-duration: 0ms;
  }

  &[data-variant="compact"] .content {
    padding-top: 4px;
    padding-bottom: 4px;
  }

  &[data-virtualized] .item {
    height: var(--c6-list-view-row-height);
    min-height: var(--c6-list-view-row-height);
    overflow: hidden;
  }

  &[data-virtualized] .content {
    padding-top: 0;
    padding-bottom: 0;
  }

  .virtualItem {
    position: absolute;
    inset-inline: 0;
    top: 0;
    transform: translateY(var(--c6-list-view-row-offset));
  }

  &[data-appearance="embedded"] .header {
    background: transparent;
  }

  &[data-row-appearance="raised"] {
    .header {
      border-bottom: 0;
    }

    .scroller {
      box-sizing: border-box;
      padding-top: ${listViewRaisedGeometry.leadingInset}px;
    }

    .collection {
      gap: ${listViewRaisedGeometry.gap}px;
    }

    .pinnedGroup {
      background: transparent;
      padding-bottom: ${listViewRaisedGeometry.gap}px;
    }

    .item {
      border-bottom: 0;
      border-radius: var(--c6-list-view-row-radius);
      overflow: hidden;
    }
  }

  .loadTrigger {
    height: 1px;
    margin: 0;
    pointer-events: none;
  }

  .sentinel {
    min-height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 12px;
    color: ${({ theme }) => theme.color.text.muted};
  }

  @media (forced-colors: active) {
    .item[data-selected="true"]::before {
      background: Highlight;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .item,
    .primary,
    .item::before {
      transition: none;
    }
  }
`;
